import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../api';

export type ProductNotificationType = 'fax_verification_success';

export interface ProductNotificationResponse {
  product_notification_to_show: ProductNotificationType | null;
  product_notification_id: string;
}

export interface DismissProductNotificationResponse {
  status: string;
}

/**
 * Hook to fetch the current product notification to show to the user
 */
export const useGetProductNotification = (enabled: boolean = true) => {
  return useQuery<ProductNotificationResponse, Error>({
    queryKey: ['product-notification'],
    queryFn: async () => {
      const response = await client.get<ProductNotificationResponse>('product-notifications/');
      return response.data;
    },
    enabled,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Function to dismiss a product notification
 */
const dismissProductNotification = async (
  productNotificationId: string,
): Promise<DismissProductNotificationResponse> => {
  const response = await client.post<DismissProductNotificationResponse>('product-notifications/dismiss', null, {
    params: {
      product_notification_id: productNotificationId,
    },
  });
  return response.data;
};

/**
 * Hook to dismiss a product notification with automatic cache invalidation
 */
export const useDismissProductNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dismissProductNotification,
    onSuccess: () => {
      // Invalidate the product notification query to refetch the latest data
      queryClient.invalidateQueries({ queryKey: ['product-notification'] });
    },
  });
};
